import type { DriverInfo } from '../../shared/race';

export interface PanelWidths { timingWidth: number; sidebarWidth: number }
export interface FavoriteDriver { number: number; name: string; code: string }
export type ResizeSide = 'timing' | 'sidebar';
export const DEFAULT_PANEL_WIDTHS: PanelWidths = { timingWidth: 520, sidebarWidth: 298 };
export const LAYOUT_STORAGE_KEY = 'f1live.dashboard.layout.v2';
export const FAVORITES_STORAGE_KEY = 'f1live.dashboard.favorites.v1';
const limits = { timingMin: 420, timingMax: 640, sidebarMin: 252, sidebarMax: 440, centerMin: 360 };
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(Math.max(min, max), value));

export function fitPanelWidths(input: PanelWidths, containerWidth: number): PanelWidths {
  const available = Math.max(0, containerWidth - 2 - limits.centerMin);
  let timingWidth = clamp(Number.isFinite(input.timingWidth) ? input.timingWidth : DEFAULT_PANEL_WIDTHS.timingWidth, limits.timingMin, limits.timingMax);
  const sidebarWidth = clamp(Number.isFinite(input.sidebarWidth) ? input.sidebarWidth : DEFAULT_PANEL_WIDTHS.sidebarWidth, limits.sidebarMin, Math.min(limits.sidebarMax, available - limits.timingMin));
  timingWidth = clamp(timingWidth, limits.timingMin, Math.min(limits.timingMax, available - sidebarWidth));
  return { timingWidth: Math.round(timingWidth), sidebarWidth: Math.round(sidebarWidth) };
}

export function panelWidthRange(widths: PanelWidths, containerWidth: number, side: ResizeSide) {
  const remaining = containerWidth - 2 - limits.centerMin;
  return side === 'timing'
    ? { min: limits.timingMin, max: Math.max(limits.timingMin, Math.min(limits.timingMax, remaining - widths.sidebarWidth)) }
    : { min: limits.sidebarMin, max: Math.max(limits.sidebarMin, Math.min(limits.sidebarMax, remaining - widths.timingWidth)) };
}

export function resizedPanels(widths: PanelWidths, containerWidth: number, side: ResizeSide, width: number): PanelWidths {
  const range = panelWidthRange(widths, containerWidth, side);
  return { ...widths, [side === 'timing' ? 'timingWidth' : 'sidebarWidth']: Math.round(clamp(width, range.min, range.max)) };
}

export function parsePanelWidths(serialized: string | null): PanelWidths {
  try {
    const value: unknown = JSON.parse(serialized ?? 'null');
    if (!value || typeof value !== 'object' || !('version' in value) || value.version !== 1 || !('widths' in value)) return { ...DEFAULT_PANEL_WIDTHS };
    const widths = value.widths;
    if (!widths || typeof widths !== 'object' || !('timingWidth' in widths) || !('sidebarWidth' in widths)
      || typeof widths.timingWidth !== 'number' || typeof widths.sidebarWidth !== 'number') return { ...DEFAULT_PANEL_WIDTHS };
    return fitPanelWidths({ timingWidth: widths.timingWidth, sidebarWidth: widths.sidebarWidth }, 10000);
  } catch { return { ...DEFAULT_PANEL_WIDTHS }; }
}

function normalizedName(name: string) { return name.normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('en'); }

export function isFavorite(favorite: FavoriteDriver, driver: Pick<DriverInfo, 'number' | 'name' | 'code'>) {
  const savedName = normalizedName(favorite.name), currentName = normalizedName(driver.name);
  // Number changes must preserve a favorite; number reuse must not follow a new driver.
  if (savedName && currentName) return savedName === currentName;
  return favorite.number === driver.number && favorite.code === driver.code;
}

export function parseFavorites(serialized: string | null): FavoriteDriver[] {
  try {
    const value: unknown = JSON.parse(serialized ?? 'null');
    if (!value || typeof value !== 'object' || !('version' in value) || value.version !== 1 || !('drivers' in value) || !Array.isArray(value.drivers)) return [];
    const result: FavoriteDriver[] = [];
    for (const item of value.drivers.slice(0, 100)) {
      if (!item || typeof item !== 'object' || !Number.isInteger(item.number) || item.number < 1 || item.number > 999
        || typeof item.name !== 'string' || !item.name.trim() || item.name.length > 100
        || typeof item.code !== 'string' || !item.code || item.code.length > 8) continue;
      const favorite = { number: item.number, name: item.name.trim(), code: item.code };
      if (!result.some(saved => isFavorite(saved, favorite))) result.push(favorite);
      if (result.length === 50) break;
    }
    return result;
  } catch { return []; }
}

export function toggleFavorite(favorites: FavoriteDriver[], driver: DriverInfo): FavoriteDriver[] {
  return favorites.some(favorite => isFavorite(favorite, driver))
    ? favorites.filter(favorite => !isFavorite(favorite, driver))
    : [...favorites, { number: driver.number, name: driver.name, code: driver.code }].slice(-50);
}
